/**
 * v0.5⑧ 経営会議PDCA: `GET /board-meetings/creatable-nodes` 等が返すフラットなノード一覧
 * (`{ node_id, node_code, node_type_code, node_type_label, name, parent_node_id, depth_level }[]`)
 * を扱う共通ヘルパー。
 *
 * ⚠️ `GET /nodes` を使わない (§8-182 と同型)。権限で絞られずテナント全ノードを返すため、
 * 店長が開くと他店名が見え、選ぶと空/403 になる。祖先パスはサーバで組まずクライアントで組む
 * (peer: `NodeSwitcher.tsx:163` の `pathTo()`。ただしそちらはネストしたツリー、こちらは
 * `parent_node_id` によるフラット配列のため走査方法が異なる)。
 */
export type BoardNode = {
  node_id: string;
  node_code: string;
  node_type_code: string;
  node_type_label: string;
  name: string;
  parent_node_id: string | null;
  depth_level: number;
};

/** 対象ノードから `parent_node_id` を辿って祖先パス (ルート→自分の順) を組む。
 *  一覧に祖先ノード自体が含まれない場合はそこで打ち切る (部分パスを返す、フェイルセーフ)。 */
export const boardNodePathTo = (nodes: BoardNode[], nodeId: string): BoardNode[] => {
  const byId = new Map(nodes.map((n) => [n.node_id, n]));
  const trail: BoardNode[] = [];
  let current = byId.get(nodeId);
  const seen = new Set<string>();
  while (current && !seen.has(current.node_id)) {
    seen.add(current.node_id);
    trail.unshift(current);
    current = current.parent_node_id ? byId.get(current.parent_node_id) : undefined;
  }
  return trail;
};

/** 375px 未満での祖先パス表示 (§7-1): 末尾2階層のみ。 */
export const boardNodePathLabel = (nodes: BoardNode[], nodeId: string, compact: boolean): string => {
  const trail = boardNodePathTo(nodes, nodeId);
  if (trail.length === 0) return '';
  const shown = compact && trail.length > 2 ? trail.slice(-2) : trail;
  const prefix = compact && trail.length > 2 ? '… › ' : '';
  return prefix + shown.map((n) => n.name).join(' › ');
};
